import { FC } from 'react';

import {
  VestingGraphData,
  VestingGraphPointGranularity,
} from '../../../../../hooks/vesting-task/use-vesting-graph';
import { formatVestingCalendarDate } from '../model/vesting-graph.utils';
import { useVestingGraphView } from './use-vesting-graph-view';
import { VestingGraphPlot } from './vesting-graph-plot';
import { AnimatedFixedTooltip, VestingGraphTooltip } from './vesting-graph-tooltip';
import { VestingGraphViewProvider } from './vesting-graph-view-context';

type VestingGraphProps = {
  allowHover?: boolean;
  vestingGraph: VestingGraphData;
};

/**
 * Single chart for every vesting type. The visual style comes from the granularity
 * variant; which markers appear is decided by the fields the API returned.
 * Card chrome (title / filter) lives in `VestingGraphCard`.
 */
export const VestingGraph: FC<VestingGraphProps> = ({ allowHover = true, vestingGraph }) => {
  const view = useVestingGraphView(vestingGraph, { allowHover });
  const {
    accelerationIndex,
    chartData,
    chartTooltip,
    isHoverLive,
    totalShares,
    unvestedSummary,
    vestedShares,
  } = view;

  return (
    <VestingGraphViewProvider value={{ vestingGraph, view }}>
      <div
        aria-label={`${vestingGraph.planName} vesting graph: ${vestedShares.toLocaleString('en-US')} of ${totalShares.toLocaleString('en-US')} shares vested`}
        className="flex w-full flex-col gap-4"
        role="img"
      >
        <VestingGraphPlot />

        <AnimatedFixedTooltip
          left={chartTooltip?.left ?? 0}
          open={isHoverLive && chartTooltip !== null}
          smoothPosition={vestingGraph.granularity !== VestingGraphPointGranularity.MILESTONE}
          top={chartTooltip?.top ?? 0}
        >
          {chartTooltip && (
            <VestingGraphTooltip
              accelerationDateLabel={
                vestingGraph.accelerationDate
                  ? formatVestingCalendarDate(view.format, vestingGraph.accelerationDate)
                  : undefined
              }
              accelerationTimestamp={
                accelerationIndex === null ? undefined : chartData[accelerationIndex]?.timestamp
              }
              active
              cliff={vestingGraph.cliff}
              cliffEndDateLabel={
                vestingGraph.cliff
                  ? formatVestingCalendarDate(view.format, vestingGraph.cliff.endDate)
                  : undefined
              }
              isMilestoneSchedule={
                vestingGraph.granularity === VestingGraphPointGranularity.MILESTONE
              }
              payload={chartTooltip.payload}
              totalShares={totalShares}
              unvestedSummary={unvestedSummary}
            />
          )}
        </AnimatedFixedTooltip>
      </div>
    </VestingGraphViewProvider>
  );
};
